import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import api from 'api/axiosConfig';
import API_BASE from 'configs/hostConfig';
import {
  Box,
  chakra,
  Container,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VisuallyHidden,
  Input,
  IconButton,
  useColorModeValue,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { FaInstagram, FaTwitter, FaYoutube } from 'react-icons/fa';
import { BiMailSend } from 'react-icons/bi';
import { DynivyLogo } from './Logo';

const SocialButton = ({ children, label, href }) => {
  return (
    <chakra.button
      bg={useColorModeValue('blackAlpha.100', 'whiteAlpha.100')}
      rounded={'full'}
      w={8}
      h={8}
      cursor={'pointer'}
      as={'a'}
      href={href}
      display={'inline-flex'}
      alignItems={'center'}
      justifyContent={'center'}
      transition={'background 0.3s ease'}
      _hover={{
        bg: useColorModeValue('blackAlpha.200', 'whiteAlpha.200'),
      }}
    >
      <VisuallyHidden>{label}</VisuallyHidden>
      {children}
    </chakra.button>
  );
};

SocialButton.propTypes = {
  children: PropTypes.any,
  label: PropTypes.string,
  href: PropTypes.string,
};

const ListHeader = ({ children }) => {
  return (
    <Text fontWeight={'500'} fontSize={'lg'} mb={2}>
      {children}
    </Text>
  );
};

ListHeader.propTypes = {
  children: PropTypes.any,
};

export default function Footer() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const withoutFooterPaths = ['/writing-practice'];

  for (let path of withoutFooterPaths) {
    if (location.pathname.startsWith(path)) return null;
  }

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) return 'Email is required';
    if (!emailRegex.test(value)) return 'Invalid email address';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
    } else {
      setError('');

      api
        .get(`${API_BASE}/api/user/guest-subscription?email=${email}`)
        .then((res) => {
          if (res.status == 200) {
            console.log('Successfully subscribe with email:', email);
          }
        })
        .catch(() => console.log('failed to subscribe with email: ', email));
    }
  };

  return (
    <Box
      // bg={useColorModeValue('gray.50', 'gray.900')}
      // color={useColorModeValue('gray.700', 'gray.200')}
      bg="green"
      color="white"
    >
      <Container as={Stack} maxW={'7xl'} py={10}>
        <SimpleGrid
          templateColumns={{ sm: '1fr 1fr', md: '2fr 1fr 1fr 2fr' }}
          spacing={8}
        >
          <Stack spacing={6}>
            <Box>
              <DynivyLogo logoType="logoNameInvert" />
            </Box>
            <Text fontSize={'sm'}>
              © 2025 Dynivy Academy. All rights reserved
            </Text>
            <Stack direction={'row'} spacing={6}>
              <SocialButton label={'Twitter'} href={'#'}>
                <FaTwitter />
              </SocialButton>
              <SocialButton label={'YouTube'} href={'#'}>
                <FaYoutube />
              </SocialButton>
              <SocialButton label={'Instagram'} href={'#'}>
                <FaInstagram />
              </SocialButton>
            </Stack>
          </Stack>
          <Stack align={'flex-start'}>
            <ListHeader>Product</ListHeader>
            {/* <Link href={'/features'}>Features</Link> */}
            {/* <Link href={'/tutorial'}>Tutorial</Link> */}
            <Link href={'/blogs'}>Blog</Link>
            <Link href={'/pricing'}>Pricing</Link>
          </Stack>
          <Stack align={'flex-start'}>
            <ListHeader>Support</ListHeader>
            <Link href={'/legal/terms-of-service'}>Terms of Service</Link>
            <Link href={'/legal/privacy-policy'}>Privacy Policy</Link>
            {/* <Link href={'/contact-us'}>Contact</Link> */}
            <Link href="mailto:contact@dynivy.com?subject=Contact Us">
              Contact
            </Link>
          </Stack>
          <Stack align={'flex-start'}>
            <ListHeader>Stay up to date</ListHeader>
            <form onSubmit={handleSubmit}>
              <Stack direction={'row'}>
                <FormControl isInvalid={error} mb="4">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Email"
                    placeholder={'Your email address'}
                    bg={useColorModeValue('blackAlpha.100', 'whiteAlpha.100')}
                    border={0}
                    _focus={{
                      bg: 'whiteAlpha.300',
                    }}
                  />
                  <FormErrorMessage>{error}</FormErrorMessage>
                </FormControl>

                <IconButton
                  type="submit"
                  bg={useColorModeValue('green.400', 'green.800')}
                  color={useColorModeValue('white', 'gray.800')}
                  _hover={{
                    bg: 'green.600',
                  }}
                  aria-label="Subscribe"
                  icon={<BiMailSend />}
                />
              </Stack>
            </form>
          </Stack>
        </SimpleGrid>
      </Container>
    </Box>
  );
}
